// CONNECT ACC, TWO WAYS, LIKE PROCORE (owner 2026-10-25: "add the connector as primary and
// the admin path as extra option like procore has multiple options of connections"):
//  - Browser connector (first): the BidLight Connector extension pairs itself from this
//    page and forwards the session of the ACC web app signed in in this browser. Works
//    for any Autodesk account - no ACC admin needed.
//  - Autodesk sign-in: our APS app's sign-in, only when the server has the app keys. Its
//    token sees the company's hubs only after an ACC account admin adds the BidLight app
//    under Custom Integrations.
// openAccConnect() mounts the window on its own, so any button can open it.
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Segmented, Spin } from "antd";
import addNotification from "helpers/notify";
import { url, tokenRef } from "api";
import { getAccStatus, getAccConnectUrl } from "./accApi";
import { refreshProviderStatus } from "components/Integrations/useIntegrations";

async function downloadExtension() {
  try {
    const r = await fetch(`${url}/acc/extension.zip`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
    if (!r.ok) throw new Error(`The download was refused (${r.status})`);
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bidlight-connector.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  } catch (e) {
    addNotification(e.message || "Could not download the connector", "danger");
  }
}

/** The Autodesk sign-in: the browser goes to Autodesk and comes back to the personal area. */
export async function signInAcc(connection = "") {
  try {
    const r = await getAccConnectUrl(connection);
    if (r && r.url) window.location.href = r.url;
  } catch (e) {
    addNotification(e.message || "Could not start the Autodesk sign-in", "danger");
  }
}

// the connector pairs the browser with the company's own (first) account, so an
// ADDITIONAL account is connected with the sign-in
export const accConnectOptions = (oauth, connection) => [
  ...(connection ? [] : [{ value: "extension", label: "Browser connector" }]),
  ...(oauth ? [{ value: "oauth", label: "Autodesk sign-in (ACC admin)" }] : []),
];

export function AccConnectModal({ onClose, connection = "", status: given = null }) {
  const [status, setStatus] = useState(given);
  const [way, setWay] = useState(connection ? "oauth" : "extension");
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (given) return;
    // A FAILED CHECK IS NOT AN ANSWER. Setting oauthConfigured:false here made a request
    // that never answered indistinguishable from the server saying "the Autodesk sign-in
    // is not set up", and the modal then REMOVED that option - taking away a way out of
    // the problem at the moment it is needed. It also printed "not set up on this server"
    // as a fact. Now the failure is carried as `statusError`, both ways stay offered, and
    // the modal says what it does not know (team rule: degrade to today, and say so).
    getAccStatus().then(setStatus, (e) => setStatus({
      oauthConfigured: false,
      statusError: (e && e.message) || "could not reach the server",
    }));
  }, [given]);

  // browser connector: watch for the extension to connect the account
  useEffect(() => {
    if (way !== "extension" || !waiting) return undefined;
    const t = setInterval(async () => {
      const s = await refreshProviderStatus("acc");
      if (s && s.connected) {
        addNotification(`ACC connected${s.email ? ` as ${s.email}` : ""}`, "success");
        onClose();
      }
    }, 4000);
    return () => clearInterval(t);
  }, [way, waiting, onClose]);

  // When the check failed we do not know whether the sign-in is configured, so it is
  // OFFERED: a dead end that says "this is not set up here" when pressed is better than an
  // option that silently is not there.
  const unknown = !!(status && status.statusError);
  const options = status ? accConnectOptions(!!status.oauthConfigured || unknown, connection) : [];

  return (
    <Modal open title="Connect ACC" onCancel={onClose} footer={null} width={640} destroyOnClose>
      {!status ? <Spin /> : null}
      {unknown ? (
        <p className="text-muted" data-testid="acc-connect-status-error">
          Could not check this server&apos;s ACC settings ({status.statusError}). Both ways are offered below; if the Autodesk sign-in is
          not set up here, it will say so when you try it.
        </p>
      ) : null}
      {status && !options.length ? (
        <p className="text-muted" data-testid="acc-connect-none">
          A further Autodesk account is connected with the Autodesk sign-in, which is not set up on this server. The company&apos;s first
          account connects with the browser connector.
        </p>
      ) : null}
      {options.length > 1 ? <Segmented block options={options} value={way} onChange={setWay} className="mb-3" /> : null}
      {status && way === "extension" && options.some((o) => o.value === "extension") ? (
        <div data-testid="acc-connect-extension">
          <p className="mb-2">
            The BidLight Connector uses the Autodesk Construction Cloud session already signed in in this browser and keeps it fresh. It
            works with any Autodesk account - no ACC account admin needed - and never sees your Autodesk password. The same connector
            carries Procore.
          </p>
          <ol className="pl-3 mb-2">
            <li>
              <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={downloadExtension}>
                Download the connector
              </button>{" "}
              and unzip it (already have the Procore one? Load this newer one in its place).
            </li>
            <li>
              Open <code>chrome://extensions</code> (or <code>edge://extensions</code>), turn on Developer mode, click{" "}
              <b>Load unpacked</b> and pick the unzipped folder.
            </li>
            <li>
              Open <a href="https://acc.autodesk.com" target="_blank" rel="noopener noreferrer">acc.autodesk.com</a> in this browser, signed in.
            </li>
            <li>Reload this page. The connector pairs itself and BidLight shows ACC as connected.</li>
          </ol>
          <button className="header-btn text-white bg-bid border-bid px-3 mt-2" disabled={waiting} onClick={() => setWaiting(true)}>
            {waiting ? "Waiting for the connector..." : "I've installed it - check now"}
          </button>
        </div>
      ) : null}
      {status && way === "oauth" && options.some((o) => o.value === "oauth") ? (
        <div data-testid="acc-connect-oauth">
          <p>
            Sign in on Autodesk&apos;s own page; you come back here connected. BidLight sees your company&apos;s hubs this way only once an
            ACC account admin has added the BidLight app under <b>Account Admin &gt; Custom Integrations</b>
            {status.clientId ? (
              <>
                {" "}
                (client ID <code>{status.clientId}</code>)
              </>
            ) : null}
            . Without that, use the browser connector.
          </p>
          <button className="header-btn text-white bg-bid border-bid px-3" onClick={() => signInAcc(connection)}>
            Sign in with Autodesk
          </button>
        </div>
      ) : null}
    </Modal>
  );
}

/** Open the connect window from anywhere. `status` is the /acc/status answer, if known. */
//
// { connection: 'new' } connects an ADDITIONAL Autodesk account for the company; an id
// reconnects that one.
export function openAccConnect(status, { connection = "" } = {}) {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const close = () => {
    ReactDOM.unmountComponentAtNode(el);
    el.remove();
  };
  ReactDOM.render(<AccConnectModal status={status || null} connection={connection} onClose={close} />, el);
}
